import React, {
  ReactNode,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import { Box } from '@wix/design-system';
import { Filter } from '@wix/bex-core';
import { runInAction } from 'mobx';
import { FilterContextProvider } from '../FilterContext';
import { observer } from 'mobx-react-lite';
import { TabTitle } from './TabTitle';
import { useToolbarCollectionContext } from '../ToolbarCollectionContext';
import { TabsFilterState } from './TabsFilterState';
import { classes, st } from './TabsFilter.st.css.js';
import { TabsDropdown } from './TabsDropdown';
import { ALL_TAB_ITEM_ID, TabsFilterItemBase } from './types';
import { TabsDefault } from './TabsDefault';

export interface TabsFilterProps<V> {
  /**
   * List of items to select from. It's recommended to use only up to 4 tabs due to horizontal space. For cases of more than 4 items use [`<SingleSelectFilter />`](/pages/cairo/?path=/story/common-filters--singleselectfilter)
   * @external
   */
  data: V[];
  /**
   * [ArrayFilter](/pages/cairo/?path=/story/common-factories--arrayfilter) state object.<br>
   * @external
   */
  filter: Filter<V[]>;
  /**
   * Adds a tab with title that indicates "All" selected (changes the filter value to an empty array).
   * @external
   */
  all?: string | null | false;

  /**
   * Render a badge next to the tab title text.<br>
   * Cairo provides ready-to-use components that automatically fetches the total for each tab: [`<TabTotalParens />`](/pages/cairo/?path=/story/infinite-scroll-table-features-tabs-filter-compounds--tabtotalcounterbadge) and [`<TabTotalCounterBadge />`](/pages/cairo/?path=/story/infinite-scroll-table-features-tabs-filter-compounds--tabtotalcounterbadge)
   * @external
   */
  renderCounterBadge?: (value: V | null | undefined) => ReactNode;
  onChange?: (value: V | null | undefined) => unknown;

  /**
   * Tabs layout type.<br>
   * - `uniformFull` - Fixed width of one tab equals available width minus body paddings divided by a number of tabs.
   * - Default (`undefined`) - size depends on label width, total cannot exceed 80% of a toolbar
   * @external
   */
  type?: 'uniformFull';

  placeAllTabLast?: boolean;
}

function _TabsFilter<V>(props: TabsFilterProps<V>) {
  const {
    type,
    filter,
    data: dataProp,
    all,
    renderCounterBadge,
    placeAllTabLast,
    onChange,
  } = props;
  const { itemKey, itemName } = filter;

  const table = useToolbarCollectionContext();

  const container = React.useRef<HTMLDivElement>();

  const [state] = useState(
    () =>
      (table.tabs ??= new TabsFilterState({
        table,
      })),
  );

  useLayoutEffect(() => {
    runInAction(() => {
      if (filter.persistent == null) {
        filter.persistent = true;
      }
    });
  }, []);

  const { responsive } = state;

  const items = useMemo<TabsFilterItemBase[]>(() => {
    const _mergedItems = dataProp.map((value, index) => {
      return {
        id: itemKey(value),
        name: itemName(value),
        title: (
          <TabTitle
            tabIndex={all && !placeAllTabLast ? index + 1 : index}
            state={responsive}
            text={itemName(value)}
            value={value}
            renderCounterBadge={renderCounterBadge}
          />
        ),
        value,
      };
    });

    if (!all) {
      return _mergedItems.map((item, index) => ({
        ...item,
        index,
        minWidth: responsive.itemsMinWidths[index],
        maxWidth: responsive.itemMaxWidth,
      }));
    }

    const allTabItem = {
      id: ALL_TAB_ITEM_ID,
      name: all,
      title: (
        <TabTitle
          tabIndex={0}
          state={responsive}
          text={all}
          value={undefined}
          renderCounterBadge={renderCounterBadge}
        />
      ),
    };

    return (
      placeAllTabLast
        ? [..._mergedItems, allTabItem]
        : [allTabItem, ..._mergedItems]
    ).map((item, index) => ({
      ...item,
      minWidth: responsive.itemsMinWidths[index],
      index,
    }));
  }, [dataProp, all, renderCounterBadge, responsive.panelMinWidth]);

  useEffect(
    () =>
      state.init({
        container: container.current,
        itemsCount: items.length,
      }),
    [],
  );

  const selectedId = filter.isEmpty
    ? ALL_TAB_ITEM_ID
    : itemKey(filter.value[0]);

  return (
    <FilterContextProvider value={filter}>
      <Box
        dataHook="collection-tabs-filter"
        className={st(classes.root)}
        width="100%"
        height={60}
        direction="vertical"
        verticalAlign="middle"
        flexGrow={1}
      >
        <div
          ref={(node) => {
            if (node) {
              container.current = node;
            }
          }}
        >
          {responsive.mode === 'tabs' ? (
            <TabsDefault
              items={items}
              data={dataProp}
              filter={filter}
              selectedId={selectedId}
              state={state}
              onChange={onChange}
              type={type}
              all={all}
            />
          ) : (
            <TabsDropdown
              filter={filter}
              data={dataProp}
              items={items}
              selectedId={selectedId}
            />
          )}
        </div>
      </Box>
    </FilterContextProvider>
  );
}

export const TabsFilter = observer(_TabsFilter);
TabsFilter.displayName = 'TabsFilter';
